import {usePowerOfAttorneysDb} from '~/use/indexedDb/powerOfAttorneysDb'
import type {PowerOfAttorney} from '~/models/powerOfAttorney'
import {type GmDtoLibraryDtoActionModelsLibraryGetRightToSignListOutput} from '~/gen/openapi/sblService'
import {featureEnabled} from "~/helpers/features";
import type {Property} from "~/models/customers";
import {QueryClient} from "@tanstack/vue-query";

const { putPowersOfAttorneyByPropertyId, getPowersOfAttorneyByPropertyId } =
  usePowerOfAttorneysDb()

const state = reactive({
  currentPropertyPowerOfAttorneys: undefined as PowerOfAttorney[] | null | undefined,
  isLoadingPowerOfAttorneys: false
})

const currentPropertyPowerOfAttorneys = computed(() => state.currentPropertyPowerOfAttorneys)
const isLoadingPowerOfAttorneys = computed(() => state.isLoadingPowerOfAttorneys)

async function fetchPowerOfAttorneys(propertyId: string, queryClient: QueryClient, propertyService: any): Promise<GmDtoLibraryDtoActionModelsLibraryGetRightToSignListOutput | undefined> {
  if (!propertyId) {
    return undefined
  }
  try {
    const queryOptions = propertyService.getPropertyRightToSign(propertyId)
    return (await queryClient.fetchQuery(queryOptions)) as GmDtoLibraryDtoActionModelsLibraryGetRightToSignListOutput
  } catch (error: any) {
    console.error(error)
  }
  return undefined
}

const updateCurrentPropertyPowerOfAttorneys = async (property: Property, queryClient: QueryClient, propertyService: any, forceUpdateFromApi = false) => {
  state.isLoadingPowerOfAttorneys = true
  try {
    const cachedInDB = await getPowersOfAttorneyByPropertyId(property.Id!)
    if (!cachedInDB?.powerOfAttorneys?.length || forceUpdateFromApi) {
      state.currentPropertyPowerOfAttorneys = await loadForProperty(property.Id!, queryClient, propertyService) as PowerOfAttorney[]
    }
    else {
      state.currentPropertyPowerOfAttorneys = cachedInDB?.powerOfAttorneys ?? []
    }
  } catch (e) {
    console.error('fetch powerofattorneys error: ', e)
    state.currentPropertyPowerOfAttorneys = null
  }
  state.isLoadingPowerOfAttorneys = false
}

const loadForProperty = async (propertyId: string, queryClient: QueryClient, propertyService: any) => {
  const runtimeConfig = useRuntimeConfig()
  const featureEnableLoadingPowerOfAttorneys = featureEnabled(runtimeConfig.public.FEATURE_ENABLE_LOADING_POWER_OF_ATTORNEYS)
  if (!featureEnableLoadingPowerOfAttorneys) {
    console.log('Loading power of attorneys has been disabled')
    return []
  }
  try {
    const fromApi = await fetchPowerOfAttorneys(propertyId || '', queryClient, propertyService)

    if (fromApi && !!fromApi.PowerOfAttorneys?.length) {
      await putPowersOfAttorneyByPropertyId({
        propertyId: propertyId,
        powerOfAttorneys: fromApi.PowerOfAttorneys as PowerOfAttorney[]
      })

      return fromApi.PowerOfAttorneys
    }
    return []
  } catch (e) {
    console.error(e)
    return null
  }
}

export const usePowerOfAttorneys = () => ({
  currentPropertyPowerOfAttorneys,
  isLoadingPowerOfAttorneys,
  updateCurrentPropertyPowerOfAttorneys,
})
